import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { AppState } from '../../../../../core/reducers';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';
import { TransactionType } from '../../../compliance/enums';
import { Program, Standard } from '../../../compliance/models';
import { ProgramService, StandardService } from '../../../compliance/services';
import { Form } from '../../../forms-templates/models';
import { FormSystem } from '../../../forms-templates/enums';
import { FormService } from '../../../forms-templates/services';
import { Category, EntityType } from '../../models';
import { NotificationLevel } from '../../enums';
import { DocumentService } from '../../services';
import { CreateCategoryOnServer, UpdateCategoryOnServer } from '../../actions';
import { GeneralAutocompleteComponent } from '../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { currentUser } from '../../../../../core/auth';

@Component({
  selector: 'kt-create-category',
  templateUrl: './create-category.component.html',
})
export class CreateCategoryComponent implements AfterViewInit, OnInit {
  form: FormGroup;
  entityTypes: EntityType[];
  prevStandardIds: number[];
  NotificationLevels = Object.values(NotificationLevel);
  getProgramAutocomplete = this.programService.searchProgram.bind(this.programService);
  getStandardAutocomplete = this.standardService.searchStandard.bind(this.standardService);
  getFormAutocomplete = this.formService.findForms.bind(this.formService, {
    system: FormSystem.DOCUMENT_REVIEW,
  });
  isSuperAdmin: boolean;
  @ViewChild('formAutocomplete') formAutocomplete: GeneralAutocompleteComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { category: Category; viewMode: boolean },
    public stringUtils: StringUtilsService,
    private dialogRef: MatDialogRef<CreateCategoryComponent>,
    private store: Store<AppState>,
    private formBuilder: FormBuilder,
    private programService: ProgramService,
    private standardService: StandardService,
    private formService: FormService,
    private documentService: DocumentService,
  ) {}

  ngOnInit(): void {
    this.store.select(currentUser).subscribe((user) => {
      this.isSuperAdmin = user.userType === 'Super Admin';
    });
    this.documentService
      .getEntityTypes()
      .pipe(take(1))
      .subscribe(({ data }) => (this.entityTypes = data));

    this.form = this.formBuilder.group({
      name: [this.data.category?.name, Validators.required],
      entityTypes: [this.data.category?.entity_types, Validators.required],
      phi: [this.data.category?.phi, Validators.required],
      overridePHI: [this.data.category?.override_phi, Validators.required],
      patientOnly: [this.data.category?.patient_only, Validators.required],
      overridePatientOnly: [this.data.category?.override_patient_only, Validators.required],
      mobileVisible: [this.data.category?.mobile_visible, Validators.required],
      overrideMobileVisible: [this.data.category?.override_mobile_visible, Validators.required],
      notificationLevel: [this.data.category?.notification_level, Validators.required],
      program: [null],
      standards: [[]],
    });
  }

  ngAfterViewInit(): void {
    if (this.data.category?.id) {
      this.standardService
        .getStandardListByTransaction(this.data.category.id, TransactionType.DOCUMENT_CATEGORY)
        .pipe(take(1))
        .subscribe((standards) => {
          this.form.get('standards').patchValue(standards || []);
          this.prevStandardIds = standards?.map((standard) => standard.id) || [];
        });

      if (this.data.category.form?.id)
        this.formService
          .getFormById(this.data.category.form.id)
          .pipe(take(1))
          .subscribe((res) => {
            this.formAutocomplete?.setValue(res?.data);
            this.formAutocomplete?.updateValueAndValidity();
          });

      if (this.data.viewMode)
        Object.keys(this.form.controls).forEach((key) => this.form.get(key).disable());
      if (this.data.category?.created_by_super_admin && !this.isSuperAdmin) {
        this.form.get('name').disable();
        this.form.get('entityTypes').disable();
      }
    }
  }

  getTitle(): string {
    return `DOCS_MODULE.CATEGORY.${
      this.data.viewMode ? 'VIEW_TITLE' : this.data.category?.id ? 'UPDATE_TITLE' : 'CREATE_TITLE'
    }`;
  }

  isInvalid(name: string): boolean {
    const field = this.form.get(name);

    return field.touched && field.invalid;
  }

  getErrorMessage(label: string): string {
    return `${label} field is required`;
  }

  closeDialog(event: Event): void {
    event.stopPropagation();

    this.dialogRef.close();
  }

  compareEntityTypes(first: EntityType, second: EntityType): boolean {
    return first?.id === second?.id;
  }

  isSavedEntityType(id: number): boolean {
    return this.data.category?.entity_types?.map((type) => type.id).includes(id);
  }

  handleProgramChange(program: Program): void {
    this.form.get('program').patchValue(program);
    this.getStandardAutocomplete = this.standardService.searchStandard.bind(
      this.standardService,
      program?.id,
      false,
    );
  }

  handleStandardChange(standards: Standard[]): void {
    this.form.get('standards').patchValue(standards);
  }

  formatProgramOption(program: Program): string {
    return program?.name || '';
  }

  formatStandardOption(standard: Standard): string {
    return standard
      ? `${standard.standard_reference}<small class="text-muted ml-4">${
          standard.program?.name || ''
        }</small>`
      : '';
  }

  formatNewStandard(standard: Standard): Standard {
    return standard;
  }

  formatFormOption(form: Form): string {
    return form?.name || '';
  }

  submit(event: Event): void {
    event.stopPropagation();

    this.form.markAllAsTouched();

    if (this.form.invalid) return;

    const formValue = this.form.value;
    const compliance: { ids: number[]; is_edited?: boolean } = {
      ids: formValue.standards?.map((standard) => standard.id) || [],
    };

    if (
      this.data.category?.id &&
      this.prevStandardIds.sort().join('') !== compliance.ids.sort().join('')
    )
      compliance.is_edited = true;

    const category = new Category(
      this.data.category?.id,
      formValue.name,
      formValue.entityTypes,
      formValue.phi,
      formValue.overridePHI,
      formValue.patientOnly,
      formValue.overridePatientOnly,
      formValue.mobileVisible,
      formValue.overrideMobileVisible,
      this.data.category?.created_by_super_admin,
      formValue.notificationLevel,
      compliance,
      this.formAutocomplete?.value,
    );

    for (const [key, value] of Object.entries(category)) {
      if (typeof value === 'string') category[key] = value.trim();
    }

    this.data.category?.id
      ? this.store.dispatch(new UpdateCategoryOnServer({ category }))
      : this.store.dispatch(new CreateCategoryOnServer({ category }));

    this.dialogRef.close({ category });
  }
}
